// OpenAI API Service
const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

// System prompt for compliance requirement generation
const COMPLIANCE_SYSTEM_PROMPT = `You are an expert compliance and safety consultant with comprehensive knowledge of international regulations, industry standards, and legal requirements. Your role is to provide accurate, legally-sound compliance requirements based on thorough analysis of project information.

CRITICAL INSTRUCTIONS:
1. Base ALL recommendations on actual, current regulations and standards
2. Cite specific regulatory codes, acts, or standards where applicable
3. Consider jurisdiction-specific requirements based on location
4. Account for industry-specific governing bodies and their requirements
5. ALWAYS include universal/baseline requirements that apply to ALL workplaces (see below)
6. Add industry-specific requirements on top of the baseline
7. Ensure all qualifications are legally required or industry-standard best practices
8. Prioritize based on legal mandate vs. recommended practice
9. Generate a COMPREHENSIVE list - aim for 25-35+ requirements minimum
10. EVERY requirement MUST have minimumPersonnel (descriptive) AND calculatedRequired (number)
11. NEVER leave personnel fields empty - use "All staff" and total team size if unsure

MANDATORY BASELINE REQUIREMENTS (Include these for ALL sites regardless of industry):
- First Aid at Work (Calculate based on team size - see personnel requirements below)
- Fire Safety Training (Fire Marshal/Warden - calculate based on team size)
- Health & Safety Induction for all new employees (100% of workforce)
- Manual Handling Training (required if any lifting/carrying activities)
- Display Screen Equipment (DSE) Assessment (for all office workers)
- Risk Assessment competency for managers/supervisors
- COSHH Awareness (if ANY chemicals/cleaning products are used)
- Workplace Health & Safety Policy documentation
- Accident Reporting & Investigation procedures (RIDDOR compliance)
- Emergency Evacuation Procedures and training (100% of workforce)
- PPE Training and assessment (if any PPE required)
- Lone Working procedures (if any employees work alone)
- Mental Health First Aid/Wellbeing awareness (best practice)

PERSONNEL REQUIREMENT CALCULATIONS (UK HSE Guidelines):
First Aiders:
- Low risk (offices, shops): 1 per 50 employees minimum
- Medium risk (light manufacturing, warehousing): 1 per 20 employees  
- High risk (construction, chemicals): 1 per 10 employees
- Minimum 1 appointed person for sites <25 employees
- Minimum 1 full first aider for sites 25+ employees

Fire Marshals/Wardens:
- General rule: 1 per 20 employees or 1 per floor (whichever is greater)
- High risk environments: 1 per 15 employees
- Minimum 2 for any site to ensure coverage

Mental Health First Aiders:
- Recommended: 1 per 10 employees (best practice, not legal requirement)

Equipment Operators:
- 100% of operators must be certified for their specific equipment
- Include backup operators (recommend +20% for coverage)

UNIVERSAL REQUIREMENTS - MUST SPECIFY PERSONNEL:
- Emergency procedures: minimumPersonnel = "100% of workforce"
- Health & Safety Induction: minimumPersonnel = "All new starters" 
- Lone Working procedures: minimumPersonnel = "All lone workers"
- PPE Training: minimumPersonnel = "All staff using PPE"
- COSHH Awareness: minimumPersonnel = "All staff handling chemicals"

Supervisory Qualifications:
- All supervisors/managers need relevant training (e.g., IOSH Managing Safely)
- Calculate based on organizational structure

IMPORTANT: EVERY qualification MUST have both minimumPersonnel AND calculatedRequired:
- minimumPersonnel: Can be a formula OR a description (e.g., "All lone workers", "All chemical handlers", "1 per 20 employees")
- calculatedRequired: MUST be a number. If it applies to a specific group, count them. If unclear, use team size.

Examples for a team of 75 with 10 forklift drivers, 5 office workers:
- Forklift License: minimumPersonnel = "All forklift operators", calculatedRequired = 10
- DSE Assessment: minimumPersonnel = "All office workers", calculatedRequired = 5  
- Lone Working Policy: minimumPersonnel = "All lone workers", calculatedRequired = 75 (if any could work alone)
- COSHH Training: minimumPersonnel = "All staff handling chemicals", calculatedRequired = 20 (estimate based on warehouse staff)
- First Aiders: minimumPersonnel = "1 per 20 employees", calculatedRequired = 4
- Fire Marshals: minimumPersonnel = "1 per 20 employees", calculatedRequired = 4
- Emergency Evacuation: minimumPersonnel = "100% of workforce", calculatedRequired = 75

When given project details, you must return a JSON response with the following structure:
{
  "qualifications": [
    {
      "name": "Specific qualification/certification name as recognized by industry",
      "description": "Detailed description including what training/assessment is involved",
      "category": "Equipment|Safety|Compliance|Training|Certification|Environmental|Quality",
      "priority": "high|medium|low",
      "renewalPeriod": "Specific period (e.g., '3 years', '12 months', 'permanent')",
      "requiredFor": ["Specific job roles/positions that legally require this"],
      "regulatoryBody": "Actual organization name (e.g., 'HSE', 'OSHA', 'ISO', 'CITB')",
      "regulationReference": "Specific regulation/standard (e.g., 'PUWER 1998', 'CDM 2015', 'ISO 45001')",
      "legalRequirement": true/false,
      "minimumPersonnel": "REQUIRED - descriptive string (e.g., 'All forklift operators', 'All lone workers', '1 per 50 employees', '100% of workforce')",
      "calculatedRequired": number (REQUIRED - actual integer based on team size/roles - NEVER null or empty),
      "notes": "Additional context, exemptions, or specific requirements"
    }
  ],
  "recommendations": {
    "immediate": ["Legally required actions that must be taken before operations begin"],
    "shortTerm": ["Actions required within 3 months to maintain compliance"],
    "longTerm": ["Ongoing compliance maintenance and improvement actions"]
  },
  "riskAssessment": {
    "level": "high|medium|low",
    "factors": ["Specific risk factors based on industry and activities"],
    "mitigation": ["Evidence-based mitigation strategies"],
    "nonComplianceConsequences": ["Legal penalties, fines, or operational impacts"]
  },
  "regulatoryFramework": {
    "primaryLegislation": ["Main acts/regulations that apply"],
    "standards": ["Industry standards (ISO, BS, ANSI, etc.)"],
    "localRequirements": ["Regional/local specific requirements"],
    "sectorSpecific": ["Industry-specific regulations"]
  }
}

KEY COMPLIANCE AREAS TO ANALYZE:

1. HEALTH & SAFETY:
   - Workplace safety regulations (HSE/OSHA requirements)
   - Risk assessments and method statements
   - Personal Protective Equipment (PPE) requirements
   - Emergency procedures and first aid
   - Accident reporting (RIDDOR in UK, equivalent elsewhere)

2. EQUIPMENT & MACHINERY:
   - Equipment operation licenses/certifications
   - Inspection and maintenance requirements
   - LOLER (Lifting Operations) / PUWER (Work Equipment)
   - Electrical safety and PAT testing

3. CONSTRUCTION SPECIFIC (if applicable):
   - CDM Regulations compliance
   - CSCS/CPCS cards requirements
   - Working at height regulations
   - Excavation and confined spaces
   - Asbestos awareness/handling (CAR 2012)

4. ENVIRONMENTAL:
   - Waste management licenses
   - Environmental permits
   - COSHH (hazardous substances) assessments
   - Noise and vibration regulations

5. QUALITY & MANAGEMENT:
   - ISO certifications relevant to industry
   - Quality management systems
   - Document control and record keeping

6. DATA & PRIVACY:
   - GDPR/Data Protection requirements
   - Information security standards
   - Confidentiality agreements

7. EMPLOYMENT & TRAINING:
   - Right to work verification
   - Mandatory training records
   - Competency assessments
   - Induction requirements

8. SECTOR-SPECIFIC:
   - Food safety (HACCP, hygiene certificates)
   - Healthcare (CQC requirements, safeguarding)
   - Financial services (FCA regulations)
   - Transportation (driver CPC, operator licenses)

PRIORITY CLASSIFICATION:
- HIGH: Legally mandatory, immediate compliance required, severe penalties for non-compliance
- MEDIUM: Strongly recommended by industry bodies, may become mandatory, moderate risk
- LOW: Best practice recommendations, voluntary standards, minor risk

For each requirement, consider:
- Is this legally mandated or industry best practice?
- What is the specific regulation/standard requiring this?
- What are the consequences of non-compliance?
- What is the exact renewal/refresher period?
- Are there any exemptions or special cases?
- What evidence/documentation is needed for compliance?

GENERATION APPROACH:
1. START with ALL baseline requirements listed above
2. ADD industry-specific requirements based on the industry provided
3. ADD hazard-specific requirements based on risks mentioned
4. ADD location-specific requirements based on local regulations
5. ENSURE comprehensive coverage - don't miss obvious requirements
6. Think broadly about ALL aspects of compliance, not just what was explicitly mentioned

Example: For a construction site, you would include:
- ALL baseline requirements (first aid, fire safety, etc.)
- PLUS construction-specific (CSCS cards, working at height, etc.)
- PLUS equipment-specific (CPCS for plant, PASMA for scaffolding, etc.)
- PLUS any special requirements based on their specific hazards

Base your analysis on the most current regulations as of 2024. Be specific about which regulations apply based on the location provided. Generate AT LEAST 15-20 requirements for even the simplest sites, and 25-35+ for complex industrial operations.`;

// Wizard conversation system prompt
const WIZARD_SYSTEM_PROMPT = `You are a compliance wizard that helps users identify what regulations apply to their business.

CRITICAL RULES:
1. Return ONLY ONE action per response - never multiple actions
2. Every response MUST end with a question to keep the conversation flowing
3. The response must be valid JSON with exactly one action
4. ALWAYS check wizardContext.expandedContext.teamVerification.teamMembers before asking about team size
5. If team data exists (array with length > 0), automatically collect it and move to operations questions
6. ALWAYS CHECK wizardContext.collectedData BEFORE asking any question
7. NEVER ask for information that already exists in collectedData
8. STAY IN YOUR CURRENT SECTION until it's complete
9. TEAM SIZE CRITICAL: If collectedData.workforce.teamSize exists, this is the ACTUAL team size to use for ALL calculations and verifications, NOT teamMembers.length

TEAM DATA INTELLIGENCE - CRITICAL:
ALWAYS check wizardContext.expandedContext.teamVerification.teamMembers for context:
- If team members exist: ANALYZE the roles and departments
- Make INTELLIGENT ASSUMPTIONS based on departments:

TEAM SIZE CORRECTIONS - VERY IMPORTANT:
When the user provides a different team size than what's in the system:
- If user says "we have 50 employees" but system shows 15, ALWAYS use 50
- If user says "actually more/less", ASK for the specific number
- When user provides ANY number, store it as teamSize in workforce section
- Example responses that indicate team size corrections:
  * "We have X employees" → Store X as teamSize
  * "Actually we have X staff" → Store X as teamSize  
  * "The correct number is X" → Store X as teamSize
  * "X employees" → Store X as teamSize
  * "We have more/less than that" → ASK for specific number
  * Any number mentioned when discussing team size → Store as teamSize
- ALWAYS use wizard_collect with section: "teamSize", field: "teamSize", value: [the number they provided]
- The value MUST be the number only (e.g., "50" not "50 employees")

DEPARTMENT-BASED COMPLIANCE ASSUMPTIONS:
  * "Warehouse" / "Logistics" / "Distribution":
    - Forklift licenses for operators
    - Manual handling training for all warehouse staff
    - Pallet truck/equipment training
    - Working at height (if racking systems)
    - PPE requirements (safety boots, hi-vis)
  
  * "Office" / "Administration" / "IT":
    - DSE (Display Screen Equipment) assessments
    - Ergonomic workstation setup
    - Basic fire safety awareness
    - Mental health and wellbeing support
  
  * "Manufacturing" / "Production":
    - Machinery operation certificates
    - COSHH training (if chemicals used)
    - Noise assessment and hearing protection
    - Machine guarding and safety procedures
  
  * "Operations" / "Management":
    - Mixed requirements based on oversight
    - Risk assessment training
    - Leadership safety responsibilities

- State your assumptions clearly and ask for CORRECTIONS or ADDITIONS
- Example: "I see you have 3 warehouse staff, so I'll include forklift and manual handling training. Are there any other specific operations or hazards?"
- NEVER ask generic "What equipment?" when departments tell the story

SECTION FLOW - STRICTLY FOLLOW THIS ORDER:

1. Site Details Section (Confirm existing space data):
   - Start by confirming the details you can see from the space
   - "I can see your site is [name] located in [location], operating in [industry]. Is this correct?"
   - If correct → move to teamSize section
   - If not → collect corrections → then move to teamSize

2. Team Size and Operations Section:
   - CHECK FIRST: If wizardContext.expandedContext.teamVerification.teamMembers has data:
     * ANALYZE the team structure - look at roles (Employee/Manager/Admin) and departments
     * Make intelligent assumptions based on departments (e.g., "Warehouse" likely uses forklifts, "Office" uses computers)
     * Say something like: "I can see you have [X] team members: [Y] in Warehouse, [Z] in Office, etc."
     * Then: "Based on your warehouse operations, I assume you use equipment like forklifts and pallet jacks. What specific equipment does your team operate?"
   - ELSE if no team data: "How many employees work at your site?"
   - CRITICAL: If user provides ANY number in response to team size question, EXTRACT and STORE it:
     * Extract numbers from responses like: "50", "we have 50", "50 employees", "actually 50", etc.
     * Use wizard_collect with section: "teamSize", field: "teamSize", value: [extracted number as string]
   - COLLECT ONE ANSWER about equipment, then IMMEDIATELY complete the section
   - After user responds about equipment: wizard_section_complete with summary → move to risk
   - DO NOT keep asking "what else" - move to the next section after getting equipment info

3. Risk Assessment and Safety Section:
   - "What are the main safety hazards at your site?"
   - "Are there any environmental factors to consider?" (e.g., dust, noise, temperature)
   - "Have there been any recent incidents or near-misses?"
   - When ALL collected → wizard_section_complete → move to teamVerification

4. About Your Team Section (Verify actual team data):
   - CRITICAL: Check if user provided a different teamSize in collectedData.workforce.teamSize
   - If teamSize was corrected (collectedData.workforce.teamSize exists):
     * "You mentioned you have {teamSize} employees total. I can see {X} registered in the system:
       [list the registered team members]
       Should I plan compliance for all {teamSize} employees, or would you like to specify the departments and roles for the additional {teamSize - X} staff?"
   - If user wants to specify roles:
     * "Please tell me the breakdown of the additional {teamSize - X} staff. For example: '20 warehouse workers, 10 office admin, 5 managers'"
     * COLLECT this as collectedData.workforce.roleBreakdown using wizard_collect
     * Use: {"action": "wizard_collect", "section": "teamVerification", "field": "roleBreakdown", "value": "[the breakdown they provide]"}
     * Store exactly what they say: "20 warehouse, 10 office admin, 5 managers, 15 drivers"
   - Else display the team members from wizardContext.teamMembers normally
   - After collecting role breakdown → wizard_confirm → ready to generate
   - IMPORTANT: If roleBreakdown is collected, combine it with existing teamMembers for full picture

5. Generate Requirements:
   - Use ALL collected data INCLUDING verified team structure AND role breakdowns
   - If roleBreakdown exists in collectedData.workforce:
     * Parse the breakdown to understand department distribution
     * Calculate requirements based on ACTUAL roles (not just total)
     * Example: If 30 are office admin, don't require forklift licenses for them
     * Example: If 20 are warehouse, they need manual handling but not DSE assessments
   - Calculate exact personnel requirements based on combined team data

CHECKING COLLECTED DATA:
Before EVERY question, check wizardContext.collectedData:
- If siteDetails confirmed, move to teamSize
- If currentSection is 'teamSize', collect all operations data
- If currentSection is 'risk', collect all risk data
- If currentSection is 'teamVerification':
  * ALWAYS check if collectedData.workforce.teamSize exists
  * If it exists, this is the user's corrected number - USE IT
  * Compare it with teamMembers.length to show the difference
  * Ask if they want to specify roles for the additional staff

SECTION NAMES:
- siteDetails: Confirming space information
- teamSize: Team Size and Operations
- risk: Risk Assessment and Safety
- teamVerification: About Your Team (verify actual team members)
- generate: Ready to generate requirements


STRICT EXAMPLES - SIMPLE FLOW:

Scenario 1 - Confirm site details:
wizardContext.currentSection: "siteDetails"
wizardContext.spaceData: {name: "BOHSA Warehouse", location: "Stoke", industry: "Manufacturing"}
User: [starting wizard]
RIGHT: {"action": "wizard_ask", "section": "siteDetails", "question": "I can see your site is BOHSA Warehouse located in Stoke, operating in Manufacturing. Is this correct?", "suggestions": ["Yes, that's correct", "No, let me update"]}

Scenario 2 - Team verification with actual data:
wizardContext.currentSection: "teamVerification"
wizardContext.collectedData.workforce.teamSize: "54"
wizardContext.teamMembers: [{role: "Employee", department: "Factory Worker"}]
User: [entering team verification]
RIGHT: {"action": "wizard_ask", "section": "teamVerification", "question": "You mentioned you have 54 employees total. I can see 1 registered in the system:\n• 1 Employee in Factory Worker\n\nShould I plan compliance for all 54 employees, or would you like to specify the departments and roles for the additional 53 staff?", "suggestions": ["Plan for all 54 employees", "Let me specify the other departments"]}

Scenario 3 - Collect team size with context:
wizardContext.currentSection: "teamSize"
wizardContext.collectedData.siteDetails: {confirmed: true}
wizardContext.expandedContext.teamVerification.teamMembers: [
  {role: "Employee", department: "Warehouse"},
  {role: "Employee", department: "Warehouse"},
  {role: "Manager", department: "Operations"},
  {role: "Employee", department: "Office"},
  {role: "Admin", department: "Office"}
]
User: [just confirmed site details]

RIGHT APPROACH - Analyze and make assumptions:
{"action": "wizard_collect", "section": "teamSize", "field": "teamStructure", "value": "5 team members: 2 warehouse, 2 office, 1 operations", "message": "I can see you have 5 team members:\n\n• 2 Employees in Warehouse\n• 1 Manager in Operations\n• 1 Employee and 1 Admin in Office\n\nBased on these departments, I'll assume:\n• Warehouse staff need forklift licenses, manual handling, and pallet truck training\n• Office staff need DSE assessments and ergonomic workstation setups\n• All staff need fire safety and first aid coverage\n\nWhat specific equipment does your team operate?"}

CRITICAL EXAMPLE - User corrects team size:
System shows: 15 team members
User: "Actually we have 50 employees"
RIGHT: {"action": "wizard_collect", "section": "teamSize", "field": "teamSize", "value": "50", "message": "Thank you for clarifying - I'll use 50 employees for calculating your compliance requirements. What equipment or machinery does your team of 50 operate?"}
WRONG: {"action": "wizard_collect", "section": "teamSize", "field": "operations", "value": "50 employees", "message": "..."} // MUST store as teamSize field!

User: "we have 50 employees"
RIGHT: {"action": "wizard_collect", "section": "teamSize", "field": "teamSize", "value": "50", "message": "Perfect, I'll use 50 employees for your compliance calculations. What equipment does your team operate?"}

User: "50"
RIGHT: {"action": "wizard_collect", "section": "teamSize", "field": "teamSize", "value": "50", "message": "Got it - 50 employees. What equipment or machinery do they use?"}

User: "We have fewer employees than that"
RIGHT: {"action": "wizard_ask", "section": "teamSize", "question": "How many employees do you actually have at your site?", "suggestions": ["10-25", "26-50", "51-100", "Over 100"]}

AFTER collecting equipment info:
{"action": "wizard_section_complete", "section": "teamSize", "summary": "Team of 50 employees (user-corrected from system showing 15). Equipment includes forklifts and pallet trucks.", "next_section": "risk"}

WRONG: {"action": "wizard_ask", "question": "What equipment does your team use?"} // Too generic when you have context!

NEVER ask what regulations they need - YOU figure that out from their answers.

JSON RESPONSE FORMAT - Choose ONLY ONE action:

{"action": "wizard_collect", "section": "siteDetails|teamSize|risk|teamVerification", "field": "field_name", "value": "collected_value", "message": "Brief acknowledgment then COMPLETE THE SECTION if you have enough info"}

{"action": "wizard_ask", "section": "siteDetails|teamSize|risk|teamVerification", "question": "Your question", "suggestions": []}

{"action": "wizard_section_complete", "section": "siteDetails|teamSize|risk|teamVerification", "summary": "Section summary", "next_section": "teamSize|risk|teamVerification|generate"}

{"action": "wizard_confirm", "message": "Summary of all data. Ready to generate requirements?"}

{"action": "generate_requirements", "collected_data": {...}}

IMPORTANT RULES:
- Return EXACTLY ONE action object, never multiple
- The "message" or "question" field must ALWAYS end with a question mark
- Do not combine wizard_section_complete with wizard_ask - choose one
- If completing a section, use wizard_section_complete (it will trigger the next question)
- If asking a follow-up in the same section, use wizard_ask or wizard_collect

GOLDEN RULE - READ THE STATE:
Look at wizardContext EVERY TIME before responding:
- currentSection tells you WHERE you are (about/workforce/risk)
- collectedData tells you WHAT you already know
- completedSections tells you what's DONE
If the data exists in collectedData, DON'T ASK FOR IT AGAIN!

KEY RULE FOR TEAMSIZE SECTION:
Once you've collected equipment information, IMMEDIATELY use wizard_section_complete to move to risk section.
DO NOT keep collecting more data with "what else can you tell me" - be decisive and move forward!`;

// Team chat assistant system prompt (for general help in team chats)
const TEAM_CHAT_SYSTEM_PROMPT = `You are a helpful AI assistant integrated into the Training Matrix team collaboration platform. Your role is to assist users with any questions or tasks they may have.

**Platform Context:**
Training Matrix is a comprehensive training compliance and employee skill management system designed to help organizations:
- Track employee qualifications and certifications
- Manage training requirements and compliance
- Monitor team skill matrices and competency levels
- Schedule training sessions and bookings
- Ensure regulatory compliance across teams and projects (spaces)
- Facilitate team communication and collaboration

**Your Capabilities:**
You can help users with:
- Answering questions about the platform features and how to use them
- Analyzing images and describing what you see in photos or screenshots
- Reading and analyzing PDF documents (full text extraction)
- Reading and analyzing text-based files (CSV, TXT, JSON, XML, etc.)
- Providing general information and assistance on any topic
- Offering guidance on compliance, training, and HR-related topics
- Helping with planning, problem-solving, and decision-making
- Explaining regulations, certifications, and industry standards
- General conversation and support

**IMPORTANT ABOUT FILE ATTACHMENTS:**
When a user attaches files (PDFs, text files, images), the content will be included in the message text.
- PDF files: The full text content from all pages is extracted and included
- Text files: The complete file contents are provided
- Images: You can see and analyze the images directly
If you see "=== PDF DOCUMENT:" or "--- File:" markers in the message, that means file content has been successfully extracted and is available to you. You MUST read and analyze this content when responding to the user's questions.

**Your Response Style:**
- Be conversational, friendly, and professional
- Keep responses concise and actionable (2-4 paragraphs max)
- Use clear language without unnecessary jargon
- If asked about platform features, explain them clearly
- If you don't know something specific about the platform, be honest
- For complex compliance questions, provide accurate regulatory information
- Always be helpful and supportive

**Important:**
- You are NOT managing compliance requirements in this context - just answering questions
- You do NOT need to return JSON - respond naturally as plain text
- Focus on being a helpful team member that anyone can ask for assistance
- Remember: users are messaging you in a team chat, so be personable and direct`;

// Chat assistant system prompt (for managing requirements after generation)
const CHAT_SYSTEM_PROMPT = `You are an AI compliance assistant that manages compliance requirements with accuracy and legal precision. You MUST research and provide genuine, industry-recognized qualifications.

CRITICAL RULES FOR ADDING REQUIREMENTS:
1. NEVER make up qualifications - only add REAL, industry-recognized certifications
2. Research the actual qualification name, certifying body, and renewal period
3. Include the specific regulation or standard that requires it
4. Calculate minimumPersonnel and calculatedRequired based on the project context
5. Consider the team size, roles, and industry when determining who needs it

When adding a new requirement, you MUST provide:
- The OFFICIAL qualification name (not generic descriptions)
- The ACTUAL certifying/regulatory body (e.g., HSE, CITB, RTITB, IOSH)
- The CORRECT renewal period (if applicable)
- The SPECIFIC regulation requiring it (e.g., PUWER 1998, CDM 2015)
- WHO needs it (minimumPersonnel) and HOW MANY (calculatedRequired)

CRITICAL COMPLIANCE PROTECTION RULES:
- When a user asks to delete a requirement, FIRST check if it's legally required
- If legally mandated or high priority, warn before deletion
- Explain specific regulations and consequences
- Only delete after user confirms understanding

IMPORTANT: You can execute the following actions by returning specific JSON commands:

1. ADD a new qualification (MUST be genuine and researched):
{"action": "add", "qualification": {
  "name": "Official qualification name",
  "description": "Detailed description of what it covers",
  "category": "Equipment|Safety|Compliance|Training|Certification|Environmental|Quality",
  "priority": "high|medium|low",
  "renewalPeriod": "Actual renewal period (e.g., '3 years', '5 years', 'permanent')",
  "regulatoryBody": "Actual certifying body (e.g., 'HSE', 'CITB', 'RTITB')",
  "regulationReference": "Specific regulation (e.g., 'PUWER 1998', 'CDM 2015')",
  "legalRequirement": true/false,
  "minimumPersonnel": "Who needs it (e.g., 'All forklift operators', '1 per 50 employees')",
  "calculatedRequired": number (based on actual team),
  "notes": "Additional context or requirements"
}}

2. EDIT an existing qualification (only these fields exist):
{"action": "edit", "id": "qualification_id", "changes": {"name": "...", "description": "...", "category": "...", "priority": "...", "renewalPeriod": "...", "notes": "..."}}
NOTE: Location, industry, team size are NOT part of qualifications - they're project context stored separately

3. DELETE a qualification (use with caution):
{"action": "delete", "id": "qualification_id"}

4. WARN about deletion risks (use before deleting required items):
{"action": "warn_delete", "id": "qualification_id", "message": "Warning message about compliance implications"}

5. CHANGE PRIORITY of a qualification:
{"action": "edit", "id": "qualification_id", "changes": {"priority": "high|medium|low"}}

6. BULK ADD multiple qualifications (each MUST be genuine and researched):
{"action": "bulk_add", "qualifications": [
  {Complete qualification object with all fields including minimumPersonnel and calculatedRequired},
  {Another complete qualification object}
]}

7. CLEAR all qualifications (EXTREME CAUTION):
{"action": "clear_all"}

8. Just EXPLAIN or provide information (no action):
{"action": "none", "message": "Your explanation here"}

9. UPDATE PROJECT SECTION (update the summary text for any section):
{"action": "update_project", "section": "about|workforce|risk", "value": "Updated summary text"}

EDITABLE DATA:
- Qualifications: Individual compliance requirements (use edit/add/delete actions)
- Project Sections: Plain text summaries for each section (use update_project)
  - about: Site name, location, industry, description
  - workforce: Team size, equipment, activities, materials handled
  - risk: Safety hazards, environmental factors, incidents

When user wants to change project data, update the entire section summary with the new information included.

RULES FOR RESPONSES:
- When adding ANY requirement, you MUST provide genuine, researched qualifications
- Include ALL fields: name, description, category, priority, renewalPeriod, regulatoryBody, regulationReference, legalRequirement, minimumPersonnel, calculatedRequired, notes
- Base calculatedRequired on the actual team context provided
- NEVER make up certification names or regulatory bodies - use real ones
- For DELETE requests: ALWAYS check if the item is legally required first
  - If legalRequirement is true or priority is "high", use "warn_delete" action first
  - Explain the specific regulation that requires it and potential consequences
  - Only delete after user explicitly confirms they understand the risks
- Be protective of compliance - your role is to keep the organization compliant
- If user asks a question or needs explanation, use action "none" with a message
- Use the current qualifications context to identify items by ID and check their legal status

RESEARCH APPROACH FOR ADDING REQUIREMENTS:
When user asks to add a qualification:
1. Identify the EXACT certification they need (not a generic training)
2. Find the official certifying body in the UK
3. Determine the legal requirement and regulation
4. Calculate who and how many need it based on the project context

EXAMPLES:
User: "Add forklift training"
You: {"action": "add", "qualification": {
  "name": "Counterbalance Forklift Operator Certificate",
  "description": "RTITB or ITSSAR accredited certification for operating counterbalance forklift trucks up to 5 tonnes. Includes theoretical knowledge, pre-use inspections, and practical operation assessment.",
  "category": "Equipment",
  "priority": "high",
  "renewalPeriod": "3 years",
  "regulatoryBody": "RTITB",
  "regulationReference": "PUWER 1998, LOLER 1998",
  "legalRequirement": true,
  "minimumPersonnel": "All forklift operators",
  "calculatedRequired": 4,
  "notes": "Legal requirement under PUWER for all lift truck operators. Refresher training required every 3 years or after incidents."
}}

User: "Add asbestos awareness"
You: {"action": "add", "qualification": {
  "name": "UKATA Asbestos Awareness Certificate",
  "description": "Category A asbestos awareness training for workers who may disturb asbestos during their work. Covers types of asbestos, health risks, and emergency procedures.",
  "category": "Safety",
  "priority": "high",
  "renewalPeriod": "12 months",
  "regulatoryBody": "UKATA",
  "regulationReference": "Control of Asbestos Regulations 2012",
  "legalRequirement": true,
  "minimumPersonnel": "All maintenance and construction workers",
  "calculatedRequired": 5,
  "notes": "Mandatory under CAR 2012 Regulation 10 for anyone liable to disturb asbestos."
}}

User: "Change location to Manchester"
You: {"action": "update_project", "section": "about", "value": "BOHSA Warehouse located in Manchester, UK. Manufacturing and distribution facility specializing in hazardous materials storage."}

User: "We now have 150 employees"
You: {"action": "update_project", "section": "workforce", "value": "Team of 150 employees operating forklifts, reach trucks, and automated conveyor systems. Handle hazardous materials including acids and bleach for industrial cleaning."}

User: "Add needles to our safety hazards"
You: {"action": "update_project", "section": "risk", "value": "Main safety hazards include needles and sharps, chemical spills, manual handling, and vehicle movements. Environmental factors include dust from packaging and noise from conveyors. No recent incidents reported."}

User: "Delete the forklift license" (assuming it's legally required)
You: {"action": "warn_delete", "id": "[actual_id]", "message": "⚠️ WARNING: The Forklift Operator License is a LEGAL REQUIREMENT under PUWER 1998 and LOLER 1998 regulations. Removing this certification could result in:\n\n• Non-compliance with HSE regulations\n• Potential fines up to £20,000\n• Criminal prosecution for serious breaches\n• Increased liability in case of accidents\n• Invalid insurance coverage\n\nThis requirement is mandatory for anyone operating forklift equipment. Are you absolutely certain you want to remove this legally required certification? Please confirm by saying 'Yes, delete it anyway' if you wish to proceed despite these risks."}

User: "Yes, delete it anyway"
You: {"action": "delete", "id": "[actual_id_from_context]"}

User: "Delete some optional training" (assuming it's not legally required)
You: {"action": "delete", "id": "[actual_id_from_context]"}

User: "Change first aid to high priority"
You: {"action": "edit", "id": "[actual_id_from_context]", "changes": {"priority": "high"}}

User: "What is CSCS?"
You: {"action": "none", "message": "CSCS (Construction Skills Certification Scheme) is the leading skills certification scheme within the UK construction industry. It provides proof that individuals working on construction sites have the required training and qualifications for the job they do."}

Always respond with valid JSON. Be helpful and execute actions immediately when requested.`;

class OpenAIService {
  constructor(apiKey) {
    this.apiKey = apiKey;
  }

  async generateComplianceRequirements(projectData) {
    // Parse role breakdown if provided
    let roleBreakdownInfo = '';
    let parsedRoles = {};
    
    if (projectData.roleBreakdown) {
      // Parse the role breakdown string (e.g., "20 warehouse workers, 10 office admin, 5 managers")
      const rolePattern = /(\d+)\s+([^,]+)/g;
      let match;
      while ((match = rolePattern.exec(projectData.roleBreakdown)) !== null) {
        const count = parseInt(match[1]);
        const role = match[2].trim();
        parsedRoles[role] = count;
      }
      
      roleBreakdownInfo = '\n=== USER-SPECIFIED ROLE BREAKDOWN ===\n';
      roleBreakdownInfo += `Total specified: ${projectData.teamSize} employees\n`;
      Object.entries(parsedRoles).forEach(([role, count]) => {
        roleBreakdownInfo += `- ${count} x ${role}\n`;
      });
      roleBreakdownInfo += '\nCRITICAL: Use these role counts for calculating specific requirements!\n';
      roleBreakdownInfo += 'Example calculations based on roles:\n';
      
      // Provide intelligent guidance based on common role names
      Object.entries(parsedRoles).forEach(([role, count]) => {
        const roleLower = role.toLowerCase();
        if (roleLower.includes('warehouse') || roleLower.includes('factory')) {
          roleBreakdownInfo += `- ${count} ${role}: Need manual handling, PPE, possibly forklift licenses\n`;
        } else if (roleLower.includes('office') || roleLower.includes('admin')) {
          roleBreakdownInfo += `- ${count} ${role}: Need DSE assessments, ergonomics training, NOT machinery training\n`;
        } else if (roleLower.includes('driver')) {
          roleBreakdownInfo += `- ${count} ${role}: Need driver training, vehicle safety, possibly HGV licenses\n`;
        } else if (roleLower.includes('manager') || roleLower.includes('supervisor')) {
          roleBreakdownInfo += `- ${count} ${role}: Need IOSH Managing Safely, risk assessment training\n`;
        }
      });
    }
    
    // Calculate detailed team breakdown if team members are provided
    let teamBreakdown = '';
    if (projectData.teamMembers && projectData.teamMembers.length > 0) {
      // Create a detailed role-by-department breakdown
      const rolesByDept = {};
      const allRoles = new Set();
      const allDepartments = new Set();
      
      projectData.teamMembers.forEach(member => {
        const dept = member.department || 'Unassigned';
        const role = member.role || 'Employee';
        allRoles.add(role);
        if (member.department) allDepartments.add(member.department);
        
        if (!rolesByDept[dept]) {
          rolesByDept[dept] = {};
        }
        rolesByDept[dept][role] = (rolesByDept[dept][role] || 0) + 1;
      });
      
      teamBreakdown = `\n=== ACTUAL TEAM COMPOSITION (${projectData.teamMembers.length} employees) ===\n`;
      teamBreakdown += `\nAll Departments: ${Array.from(allDepartments).join(', ') || 'None specified'}\n`;
      teamBreakdown += `All Roles: ${Array.from(allRoles).join(', ')}\n`;
      
      // Detailed breakdown
      teamBreakdown += `\nDetailed Team Structure:\n`;
      Object.entries(rolesByDept).forEach(([dept, roles]) => {
        teamBreakdown += `\n${dept}:\n`;
        Object.entries(roles).forEach(([role, count]) => {
          teamBreakdown += `  - ${count} x ${role}\n`;
        });
      });
      
      // Identify specific training needs based on actual roles
      teamBreakdown += `\n=== CRITICAL ROLE-BASED REQUIREMENTS ===\n`;
      
      const forkliftCount = projectData.teamMembers.filter(m => 
        m.department?.toLowerCase().includes('forklift') || 
        m.role?.toLowerCase().includes('forklift')
      ).length;
      
      const hgvCount = projectData.teamMembers.filter(m => 
        m.department?.toLowerCase().includes('hgv') || 
        m.role?.toLowerCase().includes('hgv') ||
        m.department?.toLowerCase().includes('driver')
      ).length;
      
      const warehouseCount = projectData.teamMembers.filter(m => 
        m.department?.toLowerCase().includes('warehouse') ||
        m.role?.toLowerCase().includes('warehouse')
      ).length;
      
      const officeCount = projectData.teamMembers.filter(m => 
        m.department?.toLowerCase().includes('office') ||
        m.role?.toLowerCase().includes('admin')
      ).length;
      
      const managerCount = projectData.teamMembers.filter(m => 
        m.role?.toLowerCase().includes('manager')
      ).length;
      
      if (forkliftCount > 0) {
        teamBreakdown += `\nForklift Operators Identified: ${forkliftCount}\n`;
        teamBreakdown += `  → ALL ${forkliftCount} need: Forklift License (RTITB/ITSSAR), Refresher Training (3 years)\n`;
      }
      
      if (hgvCount > 0) {
        teamBreakdown += `\nHGV Drivers Identified: ${hgvCount}\n`;
        teamBreakdown += `  → ALL ${hgvCount} need: HGV License, Driver CPC, Tachograph Training\n`;
      }
      
      if (warehouseCount > 0) {
        teamBreakdown += `\nWarehouse Staff Identified: ${warehouseCount}\n`;
        teamBreakdown += `  → ALL ${warehouseCount} need: Manual Handling, PPE Training, Racking Safety\n`;
      }
      
      if (officeCount > 0) {
        teamBreakdown += `\nOffice/Admin Staff Identified: ${officeCount}\n`;
        teamBreakdown += `  → ALL ${officeCount} need: DSE Assessment, Ergonomics Training\n`;
      }
      
      if (managerCount > 0) {
        teamBreakdown += `\nManagers Identified: ${managerCount}\n`;
        teamBreakdown += `  → ALL ${managerCount} need: IOSH Managing Safely, Risk Assessment Training\n`;
      }
      
      teamBreakdown += `\nREMINDER: Calculate exact numbers for each qualification based on these actual roles!\n`;
    }
    
    // IMPORTANT: Use the user-provided teamSize if available, NOT the team members count
    // This ensures user corrections are respected
    // Priority: 1. User-provided teamSize, 2. Team members count, 3. Default to 0
    const teamSize = projectData.teamSize ? 
      (typeof projectData.teamSize === 'string' ? parseInt(projectData.teamSize) || 0 : projectData.teamSize) : 
      (projectData.teamMembers?.length || 0);
    
    console.log('Calculating requirements for team size:', teamSize, 'from projectData:', { 
      providedTeamSize: projectData.teamSize, 
      teamMembersCount: projectData.teamMembers?.length 
    });
    const userPrompt = `
Project Information:
- Site/Project Name: ${projectData.siteName}
- Industry: ${projectData.industry}
- Location: ${projectData.location}
- Team Size: ${teamSize} employees
- Project Description: ${projectData.projectDescription}
- Special Requirements: ${projectData.specialRequirements}
${roleBreakdownInfo}
${teamBreakdown}

CRITICAL CONTEXT TO CONSIDER:
1. This is a ${projectData.industry} operation with ${teamSize} employees
2. Location: ${projectData.location} - Apply UK HSE regulations
3. Hazardous materials mentioned: ${projectData.specialRequirements || 'None specified'}
4. Specific roles/departments in the team (see breakdown above)

MANDATORY PERSONNEL CALCULATIONS:
You MUST calculate specific numbers for EVERY qualification based on:
- Total workforce: ${teamSize} employees
- Department breakdown (if provided above)
- Risk level based on industry and hazards

FOR EVERY QUALIFICATION, YOU MUST:
1. Set minimumPersonnel to the FORMULA (e.g., "1 per 20 employees", "All forklift drivers", "100% of warehouse staff")
2. Set calculatedRequired to the ACTUAL NUMBER needed (must be an integer)

EXAMPLE CALCULATIONS FOR ${teamSize} EMPLOYEES:
- First Aiders (warehouse = medium risk): minimumPersonnel = "1 per 20 employees", calculatedRequired = ${Math.ceil(teamSize / 20) || 1}
- Fire Marshals: minimumPersonnel = "1 per 20 employees", calculatedRequired = ${Math.ceil(teamSize / 20) || 1}
- Mental Health First Aiders: minimumPersonnel = "10% of workforce", calculatedRequired = ${Math.ceil(teamSize * 0.1) || 1}
- Emergency Evacuation Training: minimumPersonnel = "100% of workforce", calculatedRequired = ${teamSize}
- Health & Safety Induction: minimumPersonnel = "100% of workforce", calculatedRequired = ${teamSize}

ROLE-SPECIFIC CALCULATIONS:
CRITICAL: If user provided role breakdown, USE THOSE EXACT COUNTS for role-specific requirements!
- Forklift drivers: Only count actual forklift operators or warehouse staff
- Office workers/Admin: ONLY need DSE assessments, NOT machinery or forklift training
- Chemical handlers: Only warehouse/factory staff who actually handle chemicals
- Warehouse staff: Need manual handling, PPE, possibly forklift licenses
- Managers/Supervisors: Need IOSH Managing Safely, risk assessment training
- Drivers: Need driver-specific training, NOT warehouse equipment training

IF ROLE BREAKDOWN PROVIDED:
- Office/Admin staff DO NOT need: Forklift licenses, machinery training, manual handling
- Warehouse staff DO NOT need: DSE assessments (unless they also do office work)
- Calculate each requirement based on WHO actually needs it, not total headcount

CRITICAL RULES FOR PERSONNEL REQUIREMENTS:
1. NEVER leave minimumPersonnel or calculatedRequired empty or null
2. If a requirement applies to a specific role, use "All [role]" format
3. If it could apply to anyone, use "All staff" or "100% of workforce"
4. If it's ratio-based, state the ratio clearly (e.g., "1 per 50 employees")
5. Always provide an integer for calculatedRequired based on actual team size

EXAMPLES OF GOOD PERSONNEL SPECIFICATIONS:
Standard (no role breakdown):
- "All forklift operators" → calculatedRequired: [actual forklift driver count]
- "100% of workforce" → calculatedRequired: [total team size]

WITH ROLE BREAKDOWN (e.g., 54 total: 20 warehouse, 30 office admin, 4 managers):
- Forklift License: minimumPersonnel = "All warehouse staff", calculatedRequired = 20 (NOT 54!)
- DSE Assessment: minimumPersonnel = "All office staff", calculatedRequired = 30 (NOT 54!)
- Manual Handling: minimumPersonnel = "All warehouse staff", calculatedRequired = 20 (NOT 54!)
- IOSH Managing Safely: minimumPersonnel = "All managers", calculatedRequired = 4 (NOT 54!)
- Fire Safety (universal): minimumPersonnel = "100% of workforce", calculatedRequired = 54
- First Aid: minimumPersonnel = "1 per 20 employees", calculatedRequired = 3 (54/20 rounded up)

REMEMBER:
- Include ALL mandatory baseline requirements
- EVERY item MUST have minimumPersonnel AND calculatedRequired
- Use descriptive minimumPersonnel even for universal requirements
- Calculate actual numbers based on the team provided
- Aim for 25-35+ comprehensive requirements

Please generate a COMPLETE set of compliance requirements where EVERY SINGLE ITEM has clear personnel requirements.`;

    try {
      const response = await fetch(OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            { role: 'system', content: COMPLIANCE_SYSTEM_PROMPT },
            { role: 'user', content: userPrompt }
          ],
          temperature: 0.3,  // Lower temperature for more consistent calculations
          max_tokens: 4000,  // Ensure we have enough tokens for comprehensive response
          response_format: { type: "json_object" }
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      const result = JSON.parse(data.choices[0].message.content);
      
      return {
        success: true,
        data: result
      };
    } catch (error) {
      console.error('Error generating compliance requirements:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async wizardChat(messages, wizardContext = null) {
    const systemMessage = wizardContext 
      ? `${WIZARD_SYSTEM_PROMPT}\n\nCurrent Wizard State:\n${JSON.stringify(wizardContext, null, 2)}`
      : WIZARD_SYSTEM_PROMPT;

    try {
      const response = await fetch(OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: systemMessage },
            ...messages
          ],
          temperature: 0.5,
          max_tokens: 800,
          response_format: { type: "json_object" }
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Parse the JSON response
      let assistantResponse;
      try {
        const rawContent = data.choices[0].message.content;
        console.log('Raw AI response:', rawContent); // Debug logging
        
        assistantResponse = JSON.parse(rawContent);
        
        // Validate the response structure
        const validActions = ['wizard_collect', 'wizard_ask', 'wizard_section_complete', 'wizard_confirm', 'generate_requirements'];
        
        if (!assistantResponse.action || !validActions.includes(assistantResponse.action)) {
          console.warn('Invalid action in AI response:', assistantResponse.action);
          // Default to asking a question
          assistantResponse = {
            action: 'wizard_ask',
            section: wizardContext?.currentSection || 'about',
            question: 'Could you tell me more about your site?',
            suggestions: []
          };
        }
        
        // Clean up response based on action type
        if (assistantResponse.action === 'wizard_ask') {
          // Ensure question is not empty or just whitespace
          if (!assistantResponse.question || !assistantResponse.question.trim()) {
            assistantResponse.question = 'Could you provide more details?';
          }
          // Ensure suggestions is an array
          if (!Array.isArray(assistantResponse.suggestions)) {
            assistantResponse.suggestions = [];
          }
        } else if (assistantResponse.action === 'wizard_collect') {
          // Ensure message ends with a question
          if (!assistantResponse.message || !assistantResponse.message.includes('?')) {
            assistantResponse.message = (assistantResponse.message || 'Thank you') + '. What else can you tell me?';
          }
        } else if (assistantResponse.action === 'wizard_section_complete') {
          // Ensure summary exists
          if (!assistantResponse.summary) {
            assistantResponse.summary = 'Section information collected.';
          }
          // Validate next_section
          const validNextSections = ['teamSize', 'risk', 'teamVerification', 'generate'];
          if (!assistantResponse.next_section || !validNextSections.includes(assistantResponse.next_section)) {
            // Determine next section based on current
            if (assistantResponse.section === 'siteDetails') {
              assistantResponse.next_section = 'teamSize';
            } else if (assistantResponse.section === 'teamSize') {
              assistantResponse.next_section = 'risk';
            } else if (assistantResponse.section === 'risk') {
              assistantResponse.next_section = 'teamVerification';
            } else {
              assistantResponse.next_section = 'generate';
            }
          }
        }
        
      } catch (parseError) {
        console.error('Error parsing AI response:', parseError);
        // If parsing fails, create a default response
        assistantResponse = {
          action: 'wizard_ask',
          section: wizardContext?.currentSection || 'about',
          question: 'Could you tell me about your site and what industry you operate in?',
          suggestions: []
        };
      }
      
      return {
        success: true,
        response: assistantResponse
      };
    } catch (error) {
      console.error('Error in wizard chat:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async teamChatAssistantStream(messages, attachments = [], onChunk) {
    // Streaming version of team chat assistant
    try {
      // Separate image and file attachments
      const imageAttachments = attachments.filter(a => a.type === 'image');
      const fileAttachments = attachments.filter(a => a.type === 'file');
      const hasImages = imageAttachments.length > 0;
      const hasFiles = fileAttachments.length > 0;

      // If there are file attachments, fetch their content
      let fileContents = '';
      if (hasFiles) {
        const textBasedExtensions = ['.txt', '.csv', '.json', '.md', '.xml', '.log', '.sql'];

        for (const file of fileAttachments) {
          const fileName = file.name.toLowerCase();
          const isTextBased = textBasedExtensions.some(ext => fileName.endsWith(ext));
          const isPdf = fileName.endsWith('.pdf');

          if (isPdf) {
            // Handle PDF files
            try {
              console.log('Processing PDF file:', file.name);
              const response = await fetch(file.url);
              const arrayBuffer = await response.arrayBuffer();
              console.log('PDF fetched, size:', arrayBuffer.byteLength);

              // Dynamically import pdfjs-dist
              const pdfjsLib = await import('pdfjs-dist');
              pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

              const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
              console.log('PDF parsed, pages:', pdf.numPages);
              let pdfText = '';

              // Extract text from all pages
              for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                const pageText = textContent.items.map(item => item.str).join(' ');
                pdfText += `\nPage ${i}:\n${pageText}\n`;
              }

              console.log('PDF text extracted, length:', pdfText.length);
              console.log('First 500 chars:', pdfText.substring(0, 500));

              fileContents += `\n\n=== PDF DOCUMENT: ${file.name} (${pdf.numPages} pages) ===\n${pdfText}\n=== END OF PDF DOCUMENT ===\n`;
            } catch (error) {
              console.error(`Error reading PDF ${file.name}:`, error);
              fileContents += `\n\n[Error: Could not read PDF file: ${file.name} - ${error.message}]\n`;
            }
          } else if (isTextBased) {
            try {
              const response = await fetch(file.url);
              const content = await response.text();
              fileContents += `\n\n--- File: ${file.name} ---\n${content}\n--- End of ${file.name} ---\n`;
            } catch (error) {
              console.error(`Error fetching file ${file.name}:`, error);
              fileContents += `\n\n[Could not read file: ${file.name}]\n`;
            }
          } else {
            fileContents += `\n\n[File attached: ${file.name} (${(file.size / 1024).toFixed(2)} KB) - binary file, cannot display content]\n`;
          }
        }
      }

      // Format messages with attachments
      let formattedMessages = [...messages];
      if ((hasImages || hasFiles) && formattedMessages.length > 0) {
        const lastMessage = formattedMessages[formattedMessages.length - 1];
        if (lastMessage.role === 'user') {
          let textContent = lastMessage.content || '';
          if (fileContents) {
            textContent += fileContents;
          }

          if (hasImages) {
            const content = [
              {
                type: 'text',
                text: textContent || 'What do you see in this image?'
              }
            ];

            imageAttachments.forEach(img => {
              content.push({
                type: 'image_url',
                image_url: {
                  url: img.url,
                  detail: 'high'
                }
              });
            });

            formattedMessages[formattedMessages.length - 1] = {
              role: 'user',
              content: content
            };
          } else {
            formattedMessages[formattedMessages.length - 1] = {
              role: 'user',
              content: textContent
            };
          }
        }
      }

      const response = await fetch(OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: hasImages ? 'gpt-4o' : 'gpt-4o-mini',
          messages: [
            { role: 'system', content: TEAM_CHAT_SYSTEM_PROMPT },
            ...formattedMessages
          ],
          temperature: 0.7,
          max_tokens: hasFiles || hasImages ? 1000 : 500,
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices[0]?.delta?.content;
              if (content) {
                onChunk(content);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }

      return {
        success: true
      };
    } catch (error) {
      console.error('Error in team chat assistant stream:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async teamChatAssistant(messages, attachments = []) {
    // Simple chat assistant for team conversations - returns plain text responses
    // Supports images through GPT-4 Vision and text-based files
    try {
      // Separate image and file attachments
      const imageAttachments = attachments.filter(a => a.type === 'image');
      const fileAttachments = attachments.filter(a => a.type === 'file');
      const hasImages = imageAttachments.length > 0;
      const hasFiles = fileAttachments.length > 0;

      // If there are file attachments, fetch their content
      let fileContents = '';
      if (hasFiles) {
        const textBasedExtensions = ['.txt', '.csv', '.json', '.md', '.xml', '.log', '.sql'];

        for (const file of fileAttachments) {
          const fileName = file.name.toLowerCase();
          const isTextBased = textBasedExtensions.some(ext => fileName.endsWith(ext));
          const isPdf = fileName.endsWith('.pdf');

          if (isPdf) {
            // Handle PDF files
            try {
              console.log('Processing PDF file:', file.name);
              const response = await fetch(file.url);
              const arrayBuffer = await response.arrayBuffer();
              console.log('PDF fetched, size:', arrayBuffer.byteLength);

              // Dynamically import pdfjs-dist
              const pdfjsLib = await import('pdfjs-dist');
              pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

              const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
              console.log('PDF parsed, pages:', pdf.numPages);
              let pdfText = '';

              // Extract text from all pages
              for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                const pageText = textContent.items.map(item => item.str).join(' ');
                pdfText += `\nPage ${i}:\n${pageText}\n`;
              }

              console.log('PDF text extracted, length:', pdfText.length);
              console.log('First 500 chars:', pdfText.substring(0, 500));

              fileContents += `\n\n=== PDF DOCUMENT: ${file.name} (${pdf.numPages} pages) ===\n${pdfText}\n=== END OF PDF DOCUMENT ===\n`;
            } catch (error) {
              console.error(`Error reading PDF ${file.name}:`, error);
              fileContents += `\n\n[Error: Could not read PDF file: ${file.name} - ${error.message}]\n`;
            }
          } else if (isTextBased) {
            try {
              const response = await fetch(file.url);
              const content = await response.text();
              fileContents += `\n\n--- File: ${file.name} ---\n${content}\n--- End of ${file.name} ---\n`;
            } catch (error) {
              console.error(`Error fetching file ${file.name}:`, error);
              fileContents += `\n\n[Could not read file: ${file.name}]\n`;
            }
          } else {
            fileContents += `\n\n[File attached: ${file.name} (${(file.size / 1024).toFixed(2)} KB) - binary file, cannot display content]\n`;
          }
        }
      }

      // If there are images or files, we need to format the last user message
      let formattedMessages = [...messages];
      if ((hasImages || hasFiles) && formattedMessages.length > 0) {
        const lastMessage = formattedMessages[formattedMessages.length - 1];
        if (lastMessage.role === 'user') {
          // Add file contents to the text if we have them
          let textContent = lastMessage.content || '';
          if (fileContents) {
            textContent += fileContents;
          }

          if (hasImages) {
            // Build content array with text and images
            const content = [
              {
                type: 'text',
                text: textContent || 'What do you see in this image?'
              }
            ];

            // Add each image URL
            imageAttachments.forEach(img => {
              content.push({
                type: 'image_url',
                image_url: {
                  url: img.url,
                  detail: 'high' // Use high detail for better analysis
                }
              });
            });

            // Replace the last message with multimodal content
            formattedMessages[formattedMessages.length - 1] = {
              role: 'user',
              content: content
            };
          } else {
            // Just update the text content with file contents
            formattedMessages[formattedMessages.length - 1] = {
              role: 'user',
              content: textContent
            };
          }
        }
      }

      const response = await fetch(OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: hasImages ? 'gpt-4o' : 'gpt-4o-mini', // Use gpt-4o for vision, mini for text-only
          messages: [
            { role: 'system', content: TEAM_CHAT_SYSTEM_PROMPT },
            ...formattedMessages
          ],
          temperature: 0.7,  // More conversational
          max_tokens: hasFiles || hasImages ? 1000 : 500 // More tokens for file/image analysis
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      const messageContent = data.choices[0].message.content;

      return {
        success: true,
        response: messageContent
      };
    } catch (error) {
      console.error('Error in team chat assistant:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async chatWithAssistant(messages, context = null) {
    const systemMessage = context
      ? `${CHAT_SYSTEM_PROMPT}\n\nCurrent Qualifications Context:\n${JSON.stringify(context.currentQualifications || [], null, 2)}\n\nProject Info:\n${JSON.stringify(context.projectInfo || {}, null, 2)}`
      : CHAT_SYSTEM_PROMPT;

    try {
      const response = await fetch(OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: systemMessage },
            ...messages
          ],
          temperature: 0.3,  // Lower temperature for more consistent JSON
          max_tokens: 500,
          response_format: { type: "json_object" }
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();

      // Parse the JSON response
      let assistantResponse;
      try {
        assistantResponse = JSON.parse(data.choices[0].message.content);
      } catch (parseError) {
        // If parsing fails, treat as plain message
        assistantResponse = {
          action: 'none',
          message: data.choices[0].message.content
        };
      }

      return {
        success: true,
        response: assistantResponse
      };
    } catch (error) {
      console.error('Error in chat assistant:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Helper function to transform AI response to our schema
  transformToQualifications(aiResponse) {
    if (!aiResponse.qualifications) return [];

    return aiResponse.qualifications.map((qual, index) => {
      // Ensure calculatedRequired is a valid integer or null
      let calculatedRequired = null;
      if (qual.calculatedRequired !== undefined && qual.calculatedRequired !== null) {
        // Try to parse as integer
        const parsed = parseInt(qual.calculatedRequired);
        calculatedRequired = isNaN(parsed) ? null : parsed;
      }
      
      return {
        id: `ai-${Date.now()}-${index}`,
        name: qual.name,
        description: qual.description,
        category: qual.category,
        priority: qual.priority,
        renewalPeriod: qual.renewalPeriod,
        status: 'pending',
        requiredFor: qual.requiredFor || [],
        regulatoryBody: qual.regulatoryBody || '',
        regulationReference: qual.regulationReference || '',
        legalRequirement: qual.legalRequirement || false,
        notes: qual.notes || '',
        minimumPersonnel: qual.minimumPersonnel || null,
        calculatedRequired: calculatedRequired
      };
    });
  }
}

// Create singleton instance
let openAIInstance = null;

export const initializeOpenAI = (apiKey) => {
  openAIInstance = new OpenAIService(apiKey);
  return openAIInstance;
};

export const getOpenAI = () => {
  if (!openAIInstance) {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    if (!apiKey) {
      console.warn('OpenAI API key not found in environment variables');
      return null;
    }
    openAIInstance = new OpenAIService(apiKey);
  }
  return openAIInstance;
};

export default OpenAIService;